import {
	Alert,
	AlertIcon,
	Button,
	Checkbox,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useConsultant, useConsultantShifts } from "../../../networking/consultant.hooks";
import { useSendArbetsgivarIntyg } from "../../../networking/integration.hooks";
import AppRangePicker from "../../Core/AppRangePicker/AppRangePicker";
import AppText from "../../Core/AppText/AppText";
import { setStartOfWorkday } from "../../../utils/date.time.utils";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	consultantId: string;
}

const SendEmploymentCertificateModal: React.FC<Props> = ({ onClose, isOpen, consultantId }) => {
	const { consultant } = useConsultant({ consultantId });

	const { mutate, isLoading } = useSendArbetsgivarIntyg();
	const [hasAllowed, setHasAllowed] = useState(false);
	const [hasEndedOwnRequest, setHasEndedOwnRequest] = useState(false);
	const [reason, setReason] = useState("");
	const [range, setRange] = useState<{ start: Date; end: Date }>();
	const [success, setSuccess] = useState(false);

	function handleSubmit() {
		if (!range) return;

		mutate(
			{
				consultantId,
				hasConsultantAllowedTransmit: hasAllowed,
				endReason: hasEndedOwnRequest ? "own-request" : reason,
				sendSms: true,
				range: {
					start: setStartOfWorkday(range.start),
					end: setStartOfWorkday(range.end),
				},
			},
			{
				onSuccess: () => {
					setSuccess(true);
				},
			}
		);
	}

	const hasEnded = useMemo(() => {
		const endDate = (consultant?.profile.employment?.salary || [])[0]?.endDate;
		return endDate && dayjs(endDate).isBefore(dayjs().endOf("day"));
	}, [consultant]);

	useEffect(() => {
		if (!range && consultant) {
			// Either set end date to day that employment ended or last day of previous month
			let endDate = consultant.profile.employment?.ended
				? dayjs(consultant.profile.employment?.ended).toDate()
				: dayjs().subtract(1, "month").endOf("month").toDate();

			// Either set start date to endDate minus 13 month or the first day of employment whichever is later
			const startDate = dayjs(consultant.profile.employment?.started).isAfter(
				dayjs(consultant.profile.employment?.started).subtract(13, "month")
			)
				? dayjs(consultant.profile.employment?.started).toDate()
				: dayjs(endDate).subtract(13, "month").toDate();

			// Edge case: If start date is after end date, set end date to start date
			if (dayjs(startDate).isAfter(dayjs(endDate))) {
				endDate = startDate;
			}

			setRange({
				start: setStartOfWorkday(startDate),
				end: setStartOfWorkday(endDate),
			});
		}
	}, [range, consultant]);

	function resetForm() {
		setSuccess(false);
		setReason("");
		setHasAllowed(false);
		setHasEndedOwnRequest(false);
		setRange(undefined);
	}

	return (
		<Modal closeOnOverlayClick={false} onClose={onClose} size={"xl"} isOpen={isOpen}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Skicka till Arbetsgivarintyg.nu</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{success && (
						<VStack alignItems={"flex-start"} gap="5">
							<Alert status="success">
								<AlertIcon />
								Arbetsgivarintyget har skickats!
							</Alert>
							<AppText>
								Uppgifterna har skickats till Arbetsgivarintyg.nu. Du behöver gå in på
								Arbetsgivarintyg.nu för att signera arbetsgivarintyget.
							</AppText>
						</VStack>
					)}
					{!success && (
						<VStack alignItems={"flex-start"} gap="5">
							<VStack w={"full"} gap="3" alignItems={"flex-start"}>
								{consultant?.profile.employment?.arbetsgivarintygLastSent && (
									<Alert status="warning">
										<AlertIcon />
										Ett arbetsgivarintyg har skickats{" "}
										{dayjs(consultant?.profile.employment?.arbetsgivarintygLastSent).format(
											"YYYY-MM-DD"
										)}
									</Alert>
								)}

								<FormControl>
									<FormLabel>Tidsspann</FormLabel>
									<AppRangePicker containerMargin="0" value={range} onChange={setRange} />
									{range && <ShiftHelperText consultantId={consultantId} range={range} />}
								</FormControl>

								{hasEnded && (
									<>
										<FormControl>
											<FormLabel>Orsak till uppsägning</FormLabel>
											<Input
												placeholder="Ex: Arbetsbrist"
												value={reason}
												onChange={(e) => setReason(e.target.value)}
												disabled={hasEndedOwnRequest}
											/>
										</FormControl>
										<Checkbox
											isChecked={hasEndedOwnRequest}
											onChange={(e) => setHasEndedOwnRequest(e.currentTarget.checked)}
											size={"sm"}
										>
											Avslutat på egen begäran
										</Checkbox>
									</>
								)}
							</VStack>
						</VStack>
					)}
				</ModalBody>
				<ModalFooter mt="4">
					{success && (
						<Button
							colorScheme="green"
							onClick={() => {
								resetForm();
								onClose();
							}}
						>
							{t("CLOSE")}
						</Button>
					)}
					{!success && (
						<Flex justifyContent="space-between" w="full">
							<Checkbox
								isChecked={hasAllowed}
								onChange={(e) => setHasAllowed(e.currentTarget.checked)}
								size={"sm"}
							>
								Har godkänt att skicka till Arbetsgivarintyg.nu
							</Checkbox>
							<HStack gap="3">
								<Button
									isDisabled={!hasAllowed && !!range}
									colorScheme="green"
									onClick={handleSubmit}
									isLoading={isLoading}
								>
									{t("SEND")}
								</Button>
								<Button onClick={onClose}>{t("CANCEL")}</Button>
							</HStack>
						</Flex>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default SendEmploymentCertificateModal;

const ShiftHelperText: React.FC<{ consultantId: string; range: { start: Date; end: Date } }> = ({
	consultantId,
	range,
}) => {
	const { data: shifts } = useConsultantShifts({ consultantId, ...(range || {}) });

	return (
		<FormHelperText>
			Ange datum för arbetspass som ska inkluderas i arbetsgivarintyget. Konsulten har arbetat{" "}
			<strong>{shifts?.length}</strong> arbetspass under vald period.
		</FormHelperText>
	);
};
